/**
 * Аватар (§2.3 «<Avatar>»).
 *
 * Инициалы на детерминированном фоне из пяти оттенков серо-изумрудной гаммы:
 * один и тот же человек всегда одного цвета, иначе аватар перестаёт работать
 * как опознавательный знак в ленте истории.
 *
 * Системный автор импорта — отдельный случай из спеки: инициалы «ИМ» на
 * светло-серой плашке.
 */
import './Avatar.css'

export interface AvatarProps {
  /** ФИО: «Богдан Царелунга» → «БЦ». */
  name: string
  /** 18 — таблица и лента, 24 — карточка и шапка, 32 — профиль. */
  size?: 18 | 24 | 32
  /** Системный автор «Импорт из Google-таблицы». */
  system?: boolean
  /** Ключ окрашивания, если имена совпадают. По умолчанию — само имя. */
  colorKey?: string
  /**
   * Назначенный цвет: red, orange, amber, green, teal, blue, violet, pink.
   * Его ставят в администрировании, когда человека нужно видеть издалека —
   * например, комментарии владельца в таблице брендов.
   */
  color?: string | null
  className?: string
}

/** Цвета, которые можно назначить человеку. */
export const AVATAR_COLORS = ['red', 'orange', 'amber', 'green', 'teal', 'blue', 'violet', 'pink'] as const
export type AvatarColor = typeof AVATAR_COLORS[number]

/** Инициалы: две первые буквы слов, одно слово — одна буква. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const first = words[0]?.[0] ?? ''
  const second = words[1]?.[0] ?? ''
  return `${first}${second}`
}

/** Детерминированный выбор из пяти оттенков: сумма кодов букв по модулю 5. */
function colorIndexOf(key: string): 1 | 2 | 3 | 4 | 5 {
  let sum = 0
  for (let i = 0; i < key.length; i += 1) sum = (sum + key.charCodeAt(i)) % 5
  return (sum + 1) as 1 | 2 | 3 | 4 | 5
}

export function Avatar({ name, size = 18, system = false, colorKey, color, className }: AvatarProps) {
  const classes = ['avatar']
  if (size !== 18) classes.push(`avatar--${size}`)
  if (system) classes.push('avatar--system')
  else if (color) classes.push(`avatar--${color}`)
  else classes.push(`avatar--c${colorIndexOf(colorKey ?? name)}`)
  if (className) classes.push(className)

  return (
    <span className={classes.join(' ')} title={name}>
      <span className="visually-hidden">{name}</span>
      <span aria-hidden="true">{system ? 'ИМ' : initialsOf(name)}</span>
    </span>
  )
}
